import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    PlaneTakeoff,
    Car,
    Map as MapIcon,
    Compass,
    LayoutGrid,
    X,
    MessageCircle,
    Info,
    CalendarCheck,
    LogIn,
    LogOut,
    ShieldCheck,
    Phone,
    Mail,
    MapPin as MapPinIcon,
    ChevronRight,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import LanguageSwitcher from "../../components/LanguageSwitcher";

const TABS_DEF = [
    {
        id: "transfer",
        labelKey: "tabbar.transfer",
        icon: PlaneTakeoff,
        to: "/airport-transfers",
        match: (path) => path.startsWith("/airport-transfers") || path.startsWith("/transfers"),
    },
    {
        id: "destination",
        labelKey: "tabbar.destination",
        icon: Compass,
        to: "/destinations",
        match: (path) => path.startsWith("/destinations"),
    },
    {
        id: "tour",
        labelKey: "tabbar.tour",
        icon: MapIcon,
        to: "/tour-packages",
        match: (path) => path.startsWith("/tour-packages") || path.startsWith("/packages"),
    },
    {
        id: "cab",
        labelKey: "tabbar.cab",
        icon: Car,
        to: "/cab-booking",
        match: (path) => path === "/cab-booking" || path.startsWith("/cab-booking/") || path.startsWith("/cabs"),
    },
    {
        id: "others",
        labelKey: "tabbar.others",
        icon: LayoutGrid,
        action: "openSheet",
    },
];

const OthersSheet = ({ open, onClose }) => {
    const { user, logout } = useAuth();
    const { settings } = useData();
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    if (!open) return null;

    const go = (path) => {
        onClose();
        navigate(path);
    };

    const items = [
        {
            icon: MessageCircle,
            title: t("tabbar.contactUs"),
            sub: t("tabbar.phoneEmailOffice"),
            onClick: () => go("/contact"),
        },
        {
            icon: Info,
            title: t("tabbar.aboutSouthtrip"),
            sub: t("tabbar.ourStory"),
            onClick: () => go("/about"),
        },
        ...(user
            ? [
                  {
                      icon: CalendarCheck,
                      title: t("tabbar.myBookings"),
                      sub: t("tabbar.yourTrips"),
                      onClick: () => go("/my-bookings"),
                  },
                  {
                      icon: LogOut,
                      title: t("tabbar.signOut"),
                      sub: t("tabbar.loggedInAs", { name: user.name }),
                      onClick: () => {
                          logout();
                          onClose();
                      },
                  },
              ]
            : [
                  {
                      icon: LogIn,
                      title: t("tabbar.loginSignup"),
                      sub: t("tabbar.accessBookings"),
                      onClick: () => go("/login"),
                  },
              ]),
        {
            icon: ShieldCheck,
            title: t("tabbar.adminPanel"),
            sub: t("tabbar.staffOnly"),
            onClick: () => go("/admin/login"),
            muted: true,
        },
    ];

    const waNumber = (settings.whatsapp || "").replace(/[^\d]/g, "");

    return (
        <>
            <div
                className="fixed inset-0 z-[80] bg-[var(--ink)]/40 backdrop-blur-sm"
                onClick={onClose}
                data-testid="others-sheet-backdrop"
            />
            <div
                className="fixed left-0 right-0 bottom-0 z-[81] bg-[var(--bg)] border-t border-[var(--border)] rounded-t-3xl max-h-[86vh] overflow-y-auto shadow-2xl"
                style={{
                    paddingBottom: "calc(env(safe-area-inset-bottom, 0) + 16px)",
                    animation: "hz-rise 260ms cubic-bezier(0.2, 0.8, 0.3, 1) both",
                }}
                data-testid="others-sheet"
            >
                <div className="pt-3 pb-2 flex items-center justify-center">
                    <span className="w-10 h-1 rounded-full bg-[var(--border)]" />
                </div>
                <div className="px-6 pb-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ink-softer)]">
                            {t("tabbar.more")}
                        </div>
                        <h2 className="font-hz-display text-3xl tracking-tight mt-1">
                            {t("tabbar.everythingElse")}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="w-10 h-10 rounded-full bg-white border border-[var(--border)] flex items-center justify-center hover:bg-[var(--bg-surface)] transition"
                        data-testid="others-sheet-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-4 pb-2">
                    <div className="flex items-center justify-between p-4 rounded-2xl bg-white border border-[var(--border)]">
                        <span className="text-sm font-medium text-[var(--ink-soft)]">{t("common.language")}</span>
                        <LanguageSwitcher variant="ghost" align="left" />
                    </div>
                </div>

                <div className="px-4 pb-4 space-y-2">
                    {items.map((it) => (
                        <button
                            key={it.title}
                            onClick={it.onClick}
                            className={`w-full flex items-center gap-4 p-4 rounded-2xl bg-white border border-[var(--border)] hover:border-[var(--ink)] transition text-left ${
                                it.muted ? "opacity-70" : ""
                            }`}
                            data-testid={`others-item-${it.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        >
                            <span className="w-10 h-10 rounded-xl bg-[var(--bg-surface)] flex items-center justify-center shrink-0">
                                <it.icon className="w-5 h-5" />
                            </span>
                            <span className="flex-1">
                                <span className="font-semibold text-[var(--ink)] block">
                                    {it.title}
                                </span>
                                <span className="text-xs text-[var(--ink-softer)] block mt-0.5">
                                    {it.sub}
                                </span>
                            </span>
                            <ChevronRight className="w-4 h-4 text-[var(--ink-softer)]" />
                        </button>
                    ))}
                </div>

                <div className="mx-4 mb-6 mt-2 p-5 rounded-2xl bg-[var(--ink)] text-white">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">
                        {t("tabbar.talkToUs")}
                    </div>
                    <div className="font-hz-display text-2xl mt-1 tracking-tight">
                        {t("tabbar.needHelp")}
                    </div>
                    <div className="flex flex-col gap-2 mt-4">
                        <a
                            href={`tel:${settings.phone}`}
                            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/15 transition"
                        >
                            <Phone className="w-4 h-4" />
                            <span className="font-semibold">{settings.phone}</span>
                        </a>
                        <a
                            href={`https://wa.me/${waNumber}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#25D366] hover:bg-[#1FB855] transition"
                        >
                            <MessageCircle className="w-4 h-4" />
                            <span className="font-semibold">{t("tabbar.whatsappUs")}</span>
                        </a>
                        <a
                            href={`mailto:${settings.email}`}
                            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/15 transition"
                        >
                            <Mail className="w-4 h-4" />
                            <span className="font-semibold">{settings.email}</span>
                        </a>
                        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-white/5 text-white/80 text-xs leading-relaxed">
                            <MapPinIcon className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>{settings.address}</span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const PreviewTabBar = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const [sheetOpen, setSheetOpen] = useState(false);

    const TABS = TABS_DEF.map((tt) => ({ ...tt, label: t(tt.labelKey) }));

    const activeId = (() => {
        if (sheetOpen) return "others";
        for (const tt of TABS) {
            if (tt.match && tt.match(location.pathname, location.search)) return tt.id;
        }
        return null;
    })();
    return (
        <>
            <nav
                className="lg:hidden fixed bottom-0 left-0 right-0 z-[70] bg-white/90 backdrop-blur-xl border-t border-[var(--border)]"
                style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
                data-testid="preview-tabbar"
            >
                <ul className="grid grid-cols-5">
                    {TABS.map((tt) => {
                        const active = activeId === tt.id;
                        const common =
                            "w-full flex flex-col items-center gap-1 py-2.5 px-1 text-[10px] sm:text-[11px] font-medium transition";
                        const tone = active ? "text-[var(--brand)]" : "text-[var(--ink-soft)]";
                        const Icon = tt.icon;
                        const content = (
                            <>
                                <span
                                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-2xl flex items-center justify-center transition ${
                                        active
                                            ? "bg-[var(--ink)] text-white"
                                            : "bg-transparent text-[var(--ink-soft)]"
                                    }`}
                                >
                                    <Icon className="w-[18px] h-[18px] sm:w-5 sm:h-5" strokeWidth={active ? 2.5 : 2} />
                                </span>
                                <span className={`${active ? "font-semibold" : ""} truncate max-w-full leading-tight`}>
                                    {tt.label}
                                </span>
                            </>
                        );
                        if (tt.action === "openSheet") {
                            return (
                                <li key={tt.id}>
                                    <button
                                        type="button"
                                        onClick={() => setSheetOpen(true)}
                                        className={`${common} ${tone}`}
                                        data-testid={`tabbar-${tt.id}`}
                                    >
                                        {content}
                                    </button>
                                </li>
                            );
                        }
                        return (
                            <li key={tt.id}>
                                <Link
                                    to={tt.to}
                                    className={`${common} ${tone}`}
                                    data-testid={`tabbar-${tt.id}`}
                                >
                                    {content}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </nav>
            <OthersSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
        </>
    );
};

export default PreviewTabBar;
